/**
 * Exact decimal-string helpers for the two Partners wire scales. Both are
 * STRINGS end to end — `Number()` on either is a precision defect, so nothing
 * in this module ever converts through a JS number.
 *
 *   PartnersAmountString     ^(?:0|[1-9][0-9]{0,15})\.[0-9]{4}$   NUMERIC(20,4)
 *   PartnersPercentageString ^(?:0|[1-9][0-9]?)\.[0-9]{2}$        NUMERIC(5,2), never "0.00"
 */

/** Exact wire scale of `PartnersAmountString` (credit limits). */
export const AMOUNT_FRACTION_DIGITS = 4;
/** Exact wire scale of `PartnersPercentageString` (early-discount rate). */
export const PERCENTAGE_FRACTION_DIGITS = 2;

const ARABIC_INDIC_START = 0x0660;
const EXTENDED_ARABIC_INDIC_START = 0x06f0;

/**
 * Fold Arabic-Indic and Extended Arabic-Indic digits to ASCII, map the Arabic
 * decimal separator to ".", and drop grouping separators and whitespace.
 * Operators type in whichever numeral system their keyboard produces; the wire
 * only ever sees canonical ASCII.
 *
 * A plain comma is treated as GROUPING, not as a decimal mark: neither of the
 * app's locales (en, ar) uses it decimally, and silently reading "1,5" as
 * one-and-a-half would understate a credit limit by an order of magnitude.
 */
export function normalizeDigits(raw: string): string {
  let out = "";
  for (const char of raw) {
    const code = char.codePointAt(0) ?? 0;
    if (code >= ARABIC_INDIC_START && code <= ARABIC_INDIC_START + 9) {
      out += String(code - ARABIC_INDIC_START);
    } else if (code >= EXTENDED_ARABIC_INDIC_START && code <= EXTENDED_ARABIC_INDIC_START + 9) {
      out += String(code - EXTENDED_ARABIC_INDIC_START);
    } else if (char === "٫") {
      out += ".";
    } else if (
      char === "," ||
      char === "٬" ||
      char === "’" ||
      char === " " ||
      char === " " ||
      char === " "
    ) {
      // Grouping separators (ASCII, Arabic, apostrophe, spaces) carry no value.
      continue;
    } else {
      out += char;
    }
  }
  return out.trim();
}

const AMOUNT_RE = /^(\d{1,20})(?:\.(\d{1,4}))?$/;
const PERCENTAGE_RE = /^(\d{1,3})(?:\.(\d{1,2}))?$/;

function stripLeadingZeros(digits: string): string {
  const trimmed = digits.replace(/^0+/, "");
  return trimmed === "" ? "0" : trimmed;
}

/**
 * Canonicalize free-typed text to `PartnersAmountString` — a non-negative
 * decimal with EXACTLY four fractional digits. Returns null for anything the
 * contract would reject (negative, >4 fraction digits, >16 integer digits,
 * non-numeric), so the caller can show an inline error instead of sending it.
 */
export function toAmountString(raw: string): string | null {
  const match = AMOUNT_RE.exec(normalizeDigits(raw));
  if (match === null) return null;
  const integer = stripLeadingZeros(match[1] ?? "0");
  if (integer.length > 16) return null;
  const fraction = (match[2] ?? "").padEnd(AMOUNT_FRACTION_DIGITS, "0");
  return `${integer}.${fraction}`;
}

/**
 * Canonicalize free-typed text to `PartnersPercentageString` — 0–99 with
 * EXACTLY two fractional digits, and never the schema-forbidden "0.00".
 */
export function toPercentageString(raw: string): string | null {
  const match = PERCENTAGE_RE.exec(normalizeDigits(raw));
  if (match === null) return null;
  const integer = stripLeadingZeros(match[1] ?? "0");
  if (integer.length > 2) return null;
  const fraction = (match[2] ?? "").padEnd(PERCENTAGE_FRACTION_DIGITS, "0");
  const canonical = `${integer}.${fraction}`;
  return canonical === "0.00" ? null : canonical;
}

/** Parse a bounded non-negative integer field (net_days, discount_days…). */
export function toBoundedInteger(raw: string, min: number, max: number): number | null {
  const normalized = normalizeDigits(raw);
  if (!/^\d{1,6}$/.test(normalized)) return null;
  const value = Number.parseInt(normalized, 10);
  return value >= min && value <= max ? value : null;
}
